import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Heading, Image, Text } from "grommet";
import * as React from "react";
import { Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { usePermission, useUser } from "../account/AccountStore";
import { AliasViewModel, getAlias, IdentityUser, updateAlias, UpdateAliasRequest, updateIdentityAlias, UpdateIdentityAliasRequest } from "../aliases/AliasStore";
import { NavigationButton } from "../components/NavigationButton";
import SkeletonWrapper from "../components/SkeletonWrapper";
import StyledModal from "../components/StyledModel";
import { ResolveUsers } from "../organizations/IdentityAPI";
import AliasDetails from "./AliasDetails";
import { DeleteAliasButton } from "./DeleteAlias";

const plus = <FontAwesomeIcon icon={["fad", "plus"]}></FontAwesomeIcon>;
const edit = <FontAwesomeIcon icon={["fad", "pencil"]}></FontAwesomeIcon>;
const idBadge = <FontAwesomeIcon icon={["fad", "id-badge"]}></FontAwesomeIcon>;

const AliasProfile = () => {
    let { id } = useParams<{ id: string }>();

    let { pathname } = useLocation();
    let navigate = useNavigate();

    React.useEffect(() => {
        if (pathname == `/aliases/${id}`) {
            navigate(`/aliases/${id}/details`, { replace: true });
        }
    }, [pathname]);

    const [alias, setAlias] = React.useState({} as AliasViewModel | UpdateAliasRequest);
    const [isLoading, setIsLoading] = React.useState(true);

    let [error, setError] = React.useState("");

    const loadAlias = () => {
        return getAlias(id).then((alias) => {
            setAlias(alias);
        });
    };

    React.useEffect(() => {
        loadAlias().then(() => setIsLoading(false));
    }, []);

    const [isUpdating, setIsUpdating] = React.useState(false);

    const onChange = (updatedAlias) => {
        setAlias({ ...alias, ...updatedAlias });
    };

    let update = () => {
        setIsUpdating(true);
        updateAlias(alias).then(
            (result) => {
                loadAlias().then(() => setIsUpdating(false));
            },
            (error) => {
                setError(error);
            }
        );
    };

    let updateFromIdentity = () => {
        setIsUpdating(true);
        updateIdentityAlias({ userID: alias.identityUserID, aliasID: alias.id } as UpdateIdentityAliasRequest).then(
            (result) => {
                loadAlias().then(() => setIsUpdating(false));
            },
            (error) => {
                setError(error);
            }
        );
    };

    const [showIdentityUpdateModal, setShowIdentityUpdateModal] = React.useState(false);
    const [identityUpdates, setIdentityUpdates] = React.useState([] as any[]);
    let [identityUser, setIdentityUser] = React.useState({} as IdentityUser);

    const startUpdateFromIdentity = () => {
        ResolveUsers([alias.identityUserID]).then((res) => {
            setIdentityUser(res[0]);
        });
        setShowIdentityUpdateModal(true);
        let updates = [] as any[];

        // if (alias.email != identityUser.email) {
        //     updates.push({ field: "Email", old: alias.email, new: identityUser.email });
        // }

        if ((alias.photoUri || "") != (identityUser.photo || "")) {
            updates.push({ field: "Photo", old: alias.photoUri, new: identityUser.photo, photo: true });
        }

        setIdentityUpdates(updates);
    };

    const user = useUser();

    const hasWritePermission = usePermission("sport:aliases.write");

    const hasChanges = () => {
        return !!(alias as UpdateAliasRequest).profileContent || !!(alias as UpdateAliasRequest).photoContent || !!(alias as UpdateAliasRequest).splashContent;
    };

    return (
        <div>
            <SkeletonWrapper loading={isLoading}>
                <Heading size="small">
                    <Box direction="row" align="center" gap="small">
                        <img src={alias.photoUri} alt="" style={{ height: "auto", maxWidth: "80px", width: "auto", maxHeight: "80px", objectFit: "contain" }} />
                        {alias.name}
                    </Box>
                </Heading>

                <Box direction="row" align="center" justify="center" gap="medium">
                    <NavigationButton href={`/aliases/${alias.id}/details`} text={"Details"}></NavigationButton>
                    <Button href={"/ue/aliases/" + alias.id} label="Home Page"></Button>
                    {error && <NavigationButton href={`/aliases/${alias.id}/error`} text={"Errors"}></NavigationButton>}
                </Box>

                <Routes>
                    <Route
                        path="details"
                        element={
                            <>
                                <AliasDetails alias={alias} isBusy={!hasWritePermission || isUpdating} onChange={onChange} hasChanges={hasChanges()}></AliasDetails>
                                <br />
                                {hasWritePermission && (
                                    <Box direction="row" gap="small">
                                        <Button primary onClick={update} disabled={isUpdating || !alias.name} label="Update"></Button>
                                        {user.hasPermission("sport:aliases.delete") && <DeleteAliasButton alias={alias as AliasViewModel} onDelete={() => navigate("/aliases")}></DeleteAliasButton>}
                                        <Button onClick={startUpdateFromIdentity} disabled={isUpdating || !alias.name} label="Update From Identity"></Button>
                                    </Box>
                                )}
                                <StyledModal isOpen={showIdentityUpdateModal}>
                                    <Box pad="small">
                                        {identityUpdates.length == 0 && <Text>No updates to perform</Text>}
                                        {identityUpdates.length != 0 && <Text>Would you like to update the following?</Text>}

                                        {identityUpdates.map((update, i) => (
                                            <Box gap="small" key={i}>
                                                <Text>{update.field}</Text>
                                                {update.photo && (
                                                    <Box direction="row">
                                                        <Box fill>
                                                            <Text>Old</Text>
                                                            <Image src={update.old} width="100px" />
                                                        </Box>
                                                        <Box fill>
                                                            <Text>New</Text>
                                                            {update.new && <Image src={update.new} width="100px" />}
                                                            {!update.new && (
                                                                <Text size="small" color="gray">
                                                                    Remove Photo
                                                                </Text>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}
                                                {!update.photo && (
                                                    <Text>
                                                        {update.old} <FontAwesomeIcon icon={["fad", "arrow-alt-right"]}></FontAwesomeIcon> {update.new}
                                                    </Text>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                    {identityUpdates.length != 0 && (
                                        <Box direction="row" gap="small">
                                            <Button primary onClick={() => setShowIdentityUpdateModal(false)} label="Cancel"></Button>
                                            <Button onClick={updateFromIdentity} label="Confirm"></Button>
                                        </Box>
                                    )}
                                    {identityUpdates.length == 0 && <Button primary onClick={() => setShowIdentityUpdateModal(false)} label="Close"></Button>}
                                </StyledModal>
                            </>
                        }
                    />
                    <Route
                        path="home"
                        element={
                            <>
                                <Button href={"/ue/aliases/" + alias.id} icon={idBadge} label="View Home Page"></Button>
                            </>
                        }
                    />
                    <Route path="error" element={<pre>{JSON.stringify(error, null, 2)}</pre>} />
                </Routes>
            </SkeletonWrapper>
        </div>
    );
};

export default AliasProfile;
